import React, {useState, useEffect} from "react";
import {useForm} from "react-hook-form";
import {toast} from "react-toastify";
import {BeatLoader} from "react-spinners";
import {dbConnectionAPI} from "../../../Api/ifex";
import {FiX, FiCheckCircle, FiEye, FiEyeOff} from "react-icons/fi";

const DatabaseConnectionModal = ({visible, connection, onClose, onSuccess}) => {
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const {register, handleSubmit, watch, reset, formState: {errors, isValid}} = useForm({
        mode: "onChange",
        defaultValues: {
            name: '',
            driver: 'sqlsrv',
            host: '',
            port: '',
            database: '',
            username: '',
            password: '',
            is_active: '1',
        }
    });

    const driverOptions = [
        {value: 'sqlsrv', label: 'SQL Server'},
        {value: 'mysql', label: 'MySQL'},
        {value: 'pgsql', label: 'PostgreSQL'},
        {value: 'sqlite', label: 'SQLite'},
    ];

    useEffect(() => {
        if (!visible) return;
        reset({
            name: connection?.name || '',
            driver: connection?.driver || 'sqlsrv',
            host: connection?.host || '',
            port: connection?.port || '',
            database: connection?.database || '',
            username: connection?.username || '',
            password: '',
            is_active: connection?.is_active === false ? '0' : '1',
        });
    }, [connection, reset, visible]);

    const onSubmit = async (data) => {
        setSaving(true);
        try {
            const trimmedName = data.name?.trim();
            const trimmedHost = data.host?.trim();
            const trimmedPort = data.port?.trim();

            const payload = {
                name: trimmedName,
                connection_name: trimmedName,
                driver: data.driver || 'sqlsrv',
                host: trimmedHost,
                server: trimmedHost,
                port: trimmedPort || null,
                database: data.database?.trim(),
                username: data.username?.trim(),
                is_active: data.is_active === '1',
            };

            if (data.password) {
                payload.password = data.password;
            }

            if (connection) {
                await dbConnectionAPI.update(connection.id, payload);
                toast.success('Verbindung erfolgreich aktualisiert');
            } else {
                await dbConnectionAPI.create(payload);
                toast.success('Verbindung erfolgreich erstellt');
            }

            onSuccess();
        } catch (error) {
            console.error('Error saving connection:', error);
            if (error.response?.data?.errors) {
                Object.values(error.response.data.errors).forEach(err => {
                    toast.error(err[0]);
                });
            } else {
                toast.error('Fehler beim Speichern der Verbindung');
            }
        } finally {
            setSaving(false);
        }
    };

    const handleTestConnection = async () => {
        const formData = watch();

        // Validation for test
        if (!formData.driver || !formData.host || !formData.database || !formData.username) {
            toast.error('Bitte füllen Sie alle erforderlichen Felder aus');
            return;
        }

        if (!formData.password) {
            toast.error('Bitte geben Sie das Datenbankpasswort zum Testen ein.');
            return;
        }

        setTesting(true);
        try {
            const sanitizedHost = formData.host?.trim();
            const sanitizedPort = formData.port?.trim();

            const testData = {
                driver: formData.driver,
                host: sanitizedHost,
                server: sanitizedHost,
                port: sanitizedPort || null,
                database: formData.database?.trim(),
                username: formData.username?.trim(),
                password: formData.password,
            };

            const response = await dbConnectionAPI.test(testData);

            if (response.data.success) {
                toast.success('✓ Verbindung erfolgreich getestet!');
            } else {
                toast.error(response.data.message || 'Verbindungstest fehlgeschlagen');
            }
        } catch (error) {
            console.error('Error testing connection:', error);
            if (error.response?.data?.message) {
                toast.error(`Verbindungsfehler: ${error.response.data.message}`);
            } else {
                toast.error('Fehler beim Testen der Verbindung');
            }
        } finally {
            setTesting(false);
        }
    };

    if (!visible) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
                className="absolute inset-0 bg-modalBackDropColor bg-opacity-60"
                onClick={onClose}
            ></div>
            <div className="relative z-10 bg-white rounded-xl shadow-2xl w-11/12 max-w-2xl max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center">
                    <h2 className="text-2xl font-bold text-gray-800">
                        {connection ? 'Verbindung bearbeiten' : 'Neue Verbindung'}
                    </h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <FiX size={24} />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit(onSubmit)} className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Connection Name */}
                        <div className="md:col-span-2">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Verbindungsname *
                            </label>
                            <input
                                type="text"
                                className={`w-full px-3 py-2 border rounded ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
                                placeholder="z.B. Cobra External DB"
                                {...register('name', {
                                    required: 'Verbindungsname ist erforderlich'
                                })}
                            />
                            {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
                        </div>

                        {/* Driver */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Datenbanktyp *
                            </label>
                            <select
                                className={`w-full px-3 py-2 border rounded ${errors.driver ? 'border-red-500' : 'border-gray-300'}`}
                                {...register('driver', {required: 'Datenbanktyp ist erforderlich'})}
                            >
                                {driverOptions.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                            {errors.driver && <p className="text-red-500 text-sm mt-1">{errors.driver.message}</p>}
                        </div>

                        {/* Active Status */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Status *
                            </label>
                            <select
                                className="w-full px-3 py-2 border border-gray-300 rounded"
                                {...register('is_active', {required: true})}
                            >
                                <option value="1">Aktiv</option>
                                <option value="0">Inaktiv</option>
                            </select>
                        </div>

                        {/* Host */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Host/Server *
                            </label>
                            <input
                                type="text"
                                className={`w-full px-3 py-2 border rounded ${errors.host ? 'border-red-500' : 'border-gray-300'}`}
                                placeholder="z.B. localhost oder 192.168.1.100"
                                {...register('host', {
                                    required: 'Host ist erforderlich'
                                })}
                            />
                            {errors.host && <p className="text-red-500 text-sm mt-1">{errors.host.message}</p>}
                        </div>

                        {/* Port */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Port (optional)
                            </label>
                            <input
                                type="text"
                                className="w-full px-3 py-2 border border-gray-300 rounded"
                                placeholder="Standard-Port verwenden"
                                {...register('port')}
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Leer lassen für Standard (MySQL: 3306, SQL Server: 1433, etc.)
                            </p>
                        </div>

                        {/* Database */}
                        <div className="md:col-span-2">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Datenbankname *
                            </label>
                            <input
                                type="text"
                                className={`w-full px-3 py-2 border rounded ${errors.database ? 'border-red-500' : 'border-gray-300'}`}
                                placeholder="z.B. cobra_data"
                                {...register('database', {
                                    required: 'Datenbankname ist erforderlich'
                                })}
                            />
                            {errors.database && <p className="text-red-500 text-sm mt-1">{errors.database.message}</p>}
                        </div>

                        {/* Username */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Benutzername *
                            </label>
                            <input
                                type="text"
                                className={`w-full px-3 py-2 border rounded ${errors.username ? 'border-red-500' : 'border-gray-300'}`}
                                placeholder="Datenbankbenutzer"
                                {...register('username', {
                                    required: 'Benutzername ist erforderlich'
                                })}
                            />
                            {errors.username && <p className="text-red-500 text-sm mt-1">{errors.username.message}</p>}
                        </div>

                        {/* Password */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Passwort {connection ? '' : '*'}
                            </label>
                            <div className="relative">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    className={`w-full px-3 py-2 border rounded pr-10 ${errors.password ? 'border-red-500' : 'border-gray-300'}`}
                                    placeholder={connection ? "Leer lassen um beizubehalten" : "Datenbankpasswort"}
                                    {...register('password', {
                                        required: connection ? false : 'Passwort ist erforderlich'
                                    })}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                >
                                    {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                                </button>
                            </div>
                            {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password.message}</p>}
                            {connection && (
                                <p className="text-xs text-gray-500 mt-1">
                                    Leer lassen, um das vorhandene Passwort beizubehalten
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Test Connection Button */}
                    <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded">
                        <button
                            type="button"
                            onClick={handleTestConnection}
                            disabled={testing}
                            className={`w-full md:w-auto px-4 py-2 rounded text-white flex items-center justify-center gap-2 ${
                                testing ? 'bg-mainBlue opacity-60 cursor-not-allowed' : 'bg-mainBlue hover:bg-blue-700'
                            }`}
                        >
                            {testing ? (
                                <>
                                    <BeatLoader size={8} color="#ffffff" />
                                    <span>Teste Verbindung...</span>
                                </>
                            ) : (
                                <>
                                    <FiCheckCircle />
                                    <span>Verbindung testen</span>
                                </>
                            )}
                        </button>
                        <p className="text-xs text-blue-700 mt-2">
                            Testen Sie die Verbindung vor dem Speichern
                        </p>
                    </div>

                    {/* Footer */}
                    <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
                            disabled={saving}
                        >
                            Abbrechen
                        </button>
                        <button
                            type="submit"
                            disabled={saving || !isValid}
                            className={`px-4 py-2 rounded text-white flex items-center gap-2 ${
                                saving || !isValid ? 'bg-mainBlue opacity-60 cursor-not-allowed' : 'bg-mainBlue hover:bg-blue-700'
                            }`}
                        >
                            {saving && <BeatLoader size={8} color="#ffffff" />}
                            {saving ? 'Speichere...' : 'Speichern'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default DatabaseConnectionModal;
